'use client'

import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  StoreFrontDto,
  StoreFrontOptionsColumnLayoutDto,
} from '@b-stock/listing-api-client'

import { CompareSelectionProvider } from '@/components/listings/compare/CompareSelectionProvider'
import type { ListingGridEmptyState } from '@/components/listings/ListingGrid/ListingGrid'
import ListingGridWithFilters from '@/components/listings/ListingGrid/ListingGridWithFilters'
import { ListView } from '@/components/listings/ListView'
import type { ColumnLayoutIdEnum } from '@/components/listings/ListView/types'
import { isColumnLayoutId } from '@/components/listings/ListView/types'
import { BulkSelectionProvider } from '@/components/listings/search/BulkSelectionProvider'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'

export const ListingSearchView = ({
  emptyState,
  excludeSavedSearch,
  resultsTitle,
  storefront,
}: {
  /** Replaces the "N Results" heading in both grid and list view. */
  resultsTitle?: React.ReactNode
  /** Replaces the generic "no results" state in both grid and list view. */
  emptyState?: ListingGridEmptyState
  excludeSavedSearch?: boolean
  storefront?: StoreFrontDto
}) => {
  const { state } = useListingSearch()
  /**
   * Despite the "alp" in its LaunchDarkly key, this only gates Storefront's
   * "Download All Manifests" export now — ALP's is unconditional (see
   * enableManifestExport below). Off reverts Storefront to the old
   * page-only export, on top of each storefront's own
   * enableManifestExport setting.
   */
  const isDownloadAllManifestsEnabled = useFlag(
    FeatureFlags.ENABLE_ALP_MANIFEST_EXPORT
  )

  const { columnLayoutId, customColumnLayout } = useMemo<{
    columnLayoutId?: ColumnLayoutIdEnum
    customColumnLayout?: StoreFrontOptionsColumnLayoutDto
  }>(() => {
    if (!storefront) {
      return { columnLayoutId: 'ALL_AUCTIONS_GEN_MERCH' }
    }

    const { columnLayoutId, customColumnLayout } = storefront.options ?? {}

    return {
      columnLayoutId: isColumnLayoutId(columnLayoutId)
        ? columnLayoutId
        : 'STOREFRONT_GEN_MERCH',
      customColumnLayout,
    }
  }, [storefront])

  // Fallback to GRID as default view if state.view is bad data.
  return (
    <CompareSelectionProvider>
      <BulkSelectionProvider>
        {state.view === 'LIST' ? (
          <ListView
            columnLayoutId={columnLayoutId}
            customColumnLayout={customColumnLayout}
            enableManifestExport={
              storefront
                ? isDownloadAllManifestsEnabled &&
                  storefront.options?.enableManifestExport
                : true
            }
            emptyState={emptyState}
            excludeSavedSearch={excludeSavedSearch}
            resultsTitle={resultsTitle}
            storefront={storefront}
          />
        ) : (
          <ListingGridWithFilters
            emptyState={emptyState}
            excludeSavedSearch={excludeSavedSearch}
            resultsTitle={resultsTitle}
            storefront={storefront}
          />
        )}
      </BulkSelectionProvider>
    </CompareSelectionProvider>
  )
}
